<template>
  <div
    :class="itemClasses"
    role="button"
    :tabindex="disabled ? -1 : 0"
    :aria-current="active ? 'page' : null"
    :aria-disabled="disabled ? 'true' : null"
    @click="handleClick"
    @keydown.enter.prevent="handleClick"
    @keydown.space.prevent="handleClick"
  >
    <span v-if="$slots.prepend || icon" class="nav-list-item__prepend">
      <slot name="prepend">
        <i v-if="icon" :class="icon"></i>
      </slot>
    </span>
    <div class="nav-list-item__content">
      <span class="nav-list-item__title">
        <slot>{{ title }}</slot>
      </span>
      <span v-if="subtitle" class="nav-list-item__subtitle">{{ subtitle }}</span>
    </div>
    <span v-if="$slots.append || badge" class="nav-list-item__append">
      <slot name="append">
        <span v-if="badge" :class="['nav-badge', badgeClass]">{{ badge }}</span>
      </slot>
    </span>
  </div>
</template>

<script>
export default {
  name: 'NavListItem',
  props: {
    title: {
      type: String,
      default: ''
    },
    subtitle: {
      type: String,
      default: ''
    },
    icon: {
      type: String,
      default: null
    },
    active: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    badge: {
      type: [String, Number],
      default: null
    },
    badgeClass: {
      type: String,
      default: ''
    }
  },
  emits: ['click'],
  // 상위 NavListGroup 이 내려준 펼치기 함수 (그룹 밖이면 null)
  inject: {
    navGroupActivate: { default: null }
  },
  watch: {
    active: {
      immediate: true,
      handler(val) {
        // 활성 항목이면 자신을 감싼 그룹을 자동으로 펼친다.
        if (val && this.navGroupActivate) this.navGroupActivate()
      }
    }
  },
  computed: {
    itemClasses() {
      return [
        'nav-list-item',
        {
          'nav-list-item--active': this.active,
          'nav-list-item--disabled': this.disabled
        }
      ]
    }
  },
  methods: {
    handleClick(e) {
      if (!this.disabled) {
        this.$emit('click', e)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@use '../../scss/variables' as *;

.nav-list-item {
  display: flex;
  align-items: center;
  min-height: 40px;
  padding: $spacing-2 $spacing-3;
  margin: 1px $spacing-2;
  border-radius: $radius-md;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #475569;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 0;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    border-radius: 0 3px 3px 0;
    transition: height 0.2s ease;
  }

  &:hover:not(.nav-list-item--disabled) {
    background-color: #f1f5f9;
    color: #1e293b;
    transform: translateX(4px);

    &::before {
      height: 60%;
    }
  }

  &--active {
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%) !important;
    color: #6366f1;

    &::before {
      height: 70%;
    }

    .nav-list-item__title {
      font-weight: 600;
    }
  }

  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &__prepend {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    margin-right: $spacing-2;
    color: #64748b;
    font-size: 0.9rem;
    background: #f1f5f9;
    border-radius: $radius-sm;
    transition: all 0.2s ease;

    .nav-list-item:hover & {
      background: #e2e8f0;
      color: #6366f1;
    }

    .nav-list-item--active & {
      background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
      color: white;
    }
  }

  &__content {
    flex: 1;
    min-width: 0;
  }

  &__title {
    display: block;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.4;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    @media (max-width: 768px) {
      font-size: 14px;
    }
  }

  &__subtitle {
    display: block;
    font-size: 12px;
    color: #94a3b8;
    margin-top: 2px;
  }

  &__append {
    display: flex;
    align-items: center;
    margin-left: $spacing-2;
    color: #94a3b8;
  }
}

.nav-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  background: $color-primary;
  color: white;

  &.badge-danger {
    background: #ef4444;
    animation: pulse-badge 2s infinite;
  }

  &.badge-warning {
    background: #f59e0b;
  }
}

@keyframes pulse-badge {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}
</style>
